import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useActiveListing } from '../contexts/ActiveListingContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import VerifyPropertyModal from '../components/VerifyPropertyModal';
import { AssetTypeHub } from '../components/AssetTypeHub';
import { AppNavbar } from '../components/AppNavbar';
import { SortablePhotoGrid } from '../components/SortablePhotoGrid';
import { 
  Wand2, Link2, Loader2, CheckCircle2, Image, Video, FileText, Mail,
  DollarSign, MapPin, Bed, Bath, Square, Sparkles, RefreshCw, Edit3,
  Bookmark, Copy, Scissors, Globe, Upload, CloudCog, X, Plus,
  ChevronDown, ChevronUp, Monitor, Apple, HelpCircle
} from 'lucide-react';

export const MagicCreatePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { 
    activeListing, 
    isLoading, 
    error, 
    magicCreate, 
    setManualListing,
    setActiveListing,
    clearListing,
    loadBrandKit,
    brandKit,
    regenerateDescription,
    refreshListing
  } = useActiveListing();
  
  const [listingUrl, setListingUrl] = useState('');
  const [refreshingListing, setRefreshingListing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [manualMode, setManualMode] = useState(false);
  const [showClipper, setShowClipper] = useState(false);
  const [clipperBookmarklet, setClipperBookmarklet] = useState('');
  const [clipperLoading, setClipperLoading] = useState(false);
  const [clipFromUrl, setClipFromUrl] = useState(false);
  const bookmarkletRef = useRef(null);
  const [regeneratingDesc, setRegeneratingDesc] = useState(false);
  const [selectedDescStyle, setSelectedDescStyle] = useState('');
  const [manualData, setManualData] = useState({
    price: '',
    address: '',
    beds: '',
    baths: '',
    sqft: '',
    property_description: '',
    key_features: [],
    listing_url: '',
    photos: []
  });
  const [generatingFeatures, setGeneratingFeatures] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [pendingPropertyData, setPendingPropertyData] = useState(null);
  const [verificationFields, setVerificationFields] = useState([]);
  const [confidence, setConfidence] = useState({});
  const [showCloudImport, setShowCloudImport] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [cloudLinks, setCloudLinks] = useState('');
  const [importingCloud, setImportingCloud] = useState(false);
  const photoUploadRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    loadBrandKit();
    fetchBookmarklet();
  }, [loadBrandKit]);

  // When user is redirected here from the Dashboard Magic Bar with already-scraped data,
  // surface the Verify modal automatically if the site was blocked or fields need attention.
  useEffect(() => {
    if (!activeListing) return;
    if (showVerifyModal) return;
    if (activeListing.blocked_by_site) {
      setPendingPropertyData(activeListing);
      setVerificationFields(activeListing.verification_fields || ['price', 'beds', 'baths', 'sqft']);
      setConfidence(activeListing.confidence || {});
      setShowVerifyModal(true);
      toast.warning(
        `${activeListing.blocked_reason || 'This site blocks automated extraction'}. Use the RealProp Clipper or fill in the remaining fields.`,
        { duration: 9000 }
      );
    } else if (activeListing.partial_success || (activeListing.needs_verification && (activeListing.verification_fields?.length || 0) > 0)) {
      setPendingPropertyData(activeListing);
      setVerificationFields(activeListing.verification_fields || []);
      setConfidence(activeListing.confidence || {});
      setShowVerifyModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeListing?.listing_url]);

  // Auto-load clip data if clip_id is in URL
  useEffect(() => {
    const clipId = searchParams.get('clip_id');
    if (clipId && !activeListing) {
      loadClipData(clipId);
    }
  }, [searchParams]);

  const fetchBookmarklet = async () => {
    try {
      const response = await apiClient.get('/clipper/bookmarklet');
      setClipperBookmarklet(response.data.bookmarklet);
    } catch (e) {
      console.error('Failed to fetch bookmarklet code');
    }
  };

  // Handle local file upload for property photos
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const newPhotos = [];
    let processed = 0;

    files.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        newPhotos.push(ev.target.result);
        processed++;
        if (processed === files.length) {
          if (activeListing) {
            const existing = activeListing.photos || [];
            setManualListing({
              ...activeListing,
              photos: [...existing, ...newPhotos],
              has_real_photos: true,
            });
            toast.success(`Added ${newPhotos.length} photo${newPhotos.length > 1 ? 's' : ''}`);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Import images from Google Drive / Dropbox share links
  const handleCloudImport = async () => {
    const links = cloudLinks.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (!links.length) { toast.error('Enter at least one link'); return; }

    setImportingCloud(true);
    try {
      const res = await apiClient.post('/magic-create/import-cloud-images', { urls: links });
      if (res.data.success && res.data.photos.length > 0) {
        const existing = activeListing?.photos || [];
        setManualListing({
          ...activeListing,
          photos: [...existing, ...res.data.photos],
          has_real_photos: true,
        });
        toast.success(`Imported ${res.data.photos.length} image${res.data.photos.length > 1 ? 's' : ''}`);
        setShowCloudImport(false);
        setCloudLinks('');
      } else {
        toast.error('Could not resolve any links. Check the URLs.');
      }
      if (res.data.errors?.length) {
        res.data.errors.forEach(e => toast.warning(e));
      }
    } catch {
      toast.error('Failed to import images');
    }
    setImportingCloud(false);
  };

  // Set bookmarklet href via DOM ref (React blocks javascript: URLs)
  useEffect(() => {
    if (bookmarkletRef.current && clipperBookmarklet) {
      bookmarkletRef.current.setAttribute('href', clipperBookmarklet);
    }
  }, [clipperBookmarklet, showClipper]);

  const loadClipData = async (clipId) => {
    setClipperLoading(true);
    try {
      const response = await apiClient.get(`/magic-create/clip/${clipId}`);
      if (response.data.success) {
        const clipData = response.data.data;
        setManualListing({
          ...clipData,
          key_features: [],
          has_real_photos: clipData.photos?.length > 0,
        });
        setClipFromUrl(true);
        toast.success('Property data loaded from Clipper!');
      }
    } catch (e) {
      toast.error('Failed to load clipped data. It may have expired.');
    }
    setClipperLoading(false);
  };

  const fetchTemplates = async () => {
    try {
      const [tplRes, reelRes] = await Promise.all([
        apiClient.get('/templates'),
        apiClient.get('/reels/templates').catch(() => ({ data: { templates: [] } }))
      ]);
      const generalTemplates = tplRes.data.templates || [];
      const reelTemplates = (reelRes.data?.templates || reelRes.data || []).map(rt => ({
        ...rt,
        type: 'reel',
        thumbnail_url: rt.thumbnail_url || rt.tracks_config?.[0]?.source_url || rt.tracks?.[0]?.source_url || null,
      }));
      // Built-in carousel templates
      const carouselTemplates = [
        { template_id: 'carousel-property-showcase', name: 'Property Showcase', type: 'carousel', category: 'listing', description: '5-slide showcase with hero, features, photos, and CTA' },
        { template_id: 'carousel-feature-highlights', name: 'Feature Highlights', type: 'carousel', category: 'listing', description: 'Highlight beds, baths, sqft across slides' },
        { template_id: 'carousel-open-house', name: 'Open House', type: 'carousel', category: 'listing', description: 'Open house announcement with property details' },
      ];
      setTemplates([...generalTemplates, ...reelTemplates, ...carouselTemplates]);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const handleMagicCreate = async () => {
    if (!listingUrl) {
      toast.error('Please enter a listing URL');
      return;
    }
    
    const result = await magicCreate(listingUrl);
    if (result.success) {
      const data = result.data;
      
      // Check if site blocked automated access
      if (data.blocked_by_site) {
        setPendingPropertyData({
          ...data,
          listing_url: listingUrl
        });
        setVerificationFields(data.verification_fields || ['price', 'beds', 'baths', 'sqft']);
        setConfidence(data.confidence || {});
        setShowVerifyModal(true);
        toast.warning(
          `${data.blocked_reason || 'This site blocks automated data extraction'}. We extracted what we could from the URL — please fill in the remaining details.`,
          { duration: 8000 }
        );
      }
      // Check for partial_success flag (2+ core fields missing)
      else if (data.partial_success) {
        setPendingPropertyData({
          ...data,
          listing_url: listingUrl
        });
        setVerificationFields(data.verification_fields || []);
        setConfidence(data.confidence || {});
        setShowVerifyModal(true);
        toast.warning('Some key data could not be extracted. Please verify and fill in the missing fields.', {
          duration: 6000
        });
      } else if (data.needs_verification && data.verification_fields?.length > 0) {
        // Show verification modal with extracted data
        setPendingPropertyData({
          ...data,
          listing_url: listingUrl
        });
        setVerificationFields(data.verification_fields);
        setConfidence(data.confidence || {});
        setShowVerifyModal(true);
        toast.info('Some fields need verification. Please review and confirm.', {
          duration: 4000
        });
      } else {
        // Data looks good - proceed directly
        toast.success('Property data extracted successfully!');
      }
    } else {
      // Complete failure - show manual entry
      toast.error(result.error || 'Failed to extract property data');
      if (result.verification_fields?.length > 0) {
        // Pre-fill what we can from the URL
        setManualData(prev => ({
          ...prev,
          listing_url: listingUrl
        }));
        setManualMode(true);
      }
    }
  };

  const handleVerifyProperty = (verifiedData) => {
    // User has verified/corrected the data - push to global state
    // Preserve photos from the original scraped data
    const photosFromScrape = pendingPropertyData?.photos || [];
    const mainImageFromScrape = pendingPropertyData?.main_image_url;
    const hasRealPhotos = pendingPropertyData?.has_real_photos || false;
    
    setManualListing({
      ...verifiedData,
      listing_url: listingUrl,
      photos: photosFromScrape.length > 0 ? photosFromScrape : ['https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80'],
      main_image_url: mainImageFromScrape || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80',
      has_real_photos: hasRealPhotos
    });
    setPendingPropertyData(null);
    setShowVerifyModal(false);
    toast.success('Property data verified and saved!');
  };

  const handleManualSubmit = async () => {
    if (!manualData.price || !manualData.address) {
      toast.error('Please enter at least price and address');
      return;
    }

    let keyFeatures = manualData.key_features || [];

    // Auto-generate key features from description using AI
    if (manualData.property_description && manualData.property_description.trim().length >= 10 && keyFeatures.length === 0) {
      setGeneratingFeatures(true);
      try {
        const res = await apiClient.post('/magic-create/generate-features', {
          description: manualData.property_description,
          price: manualData.price,
          address: manualData.address,
          beds: manualData.beds,
          baths: manualData.baths,
          sqft: manualData.sqft,
        });
        if (res.data.success && res.data.key_features?.length > 0) {
          keyFeatures = res.data.key_features;
          toast.success('AI generated key features from your description!');
        }
      } catch {
        toast.warning('Could not auto-generate features. You can add them later.');
      }
      setGeneratingFeatures(false);
    }

    const userPhotos = manualData.photos || [];
    const filteredData = {
      ...manualData,
      property_summary: manualData.property_description || '',
      key_features: keyFeatures,
      photos: userPhotos,
      main_image_url: userPhotos[0] || null,
      has_real_photos: userPhotos.length > 0
    };
    setManualListing(filteredData);
    setManualMode(false);
    toast.success('Property data saved!');
  };

  const handleRegenerateDescription = async (style) => {
    setRegeneratingDesc(true);
    setSelectedDescStyle(style);
    const result = await regenerateDescription(style);
    if (result.success) {
      toast.success(`Description regenerated in ${style} style!`);
    } else {
      toast.error(result.error || 'Failed to regenerate description');
    }
    setRegeneratingDesc(false);
  };

  const handleRefreshListing = async () => {
    setRefreshingListing(true);
    const toastId = toast.loading('Refreshing listing data…');
    const result = await refreshListing();
    if (result.success) {
      toast.success('Listing data refreshed with precise address details.', { id: toastId });
    } else {
      toast.error(result.error || 'Failed to refresh listing', { id: toastId });
    }
    setRefreshingListing(false);
  };

  const formatPrice = (price) => {
    if (!price) return '';
    const num = price.toString().replace(/[^0-9]/g, '');
    return `$${parseInt(num).toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      <AppNavbar />

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mb-4">
            <Wand2 className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            RealProp Magic Import
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {activeListing 
              ? "Property data loaded! Now select a template below to create your marketing materials."
              : "Paste a listing URL or enter details manually, then select a template to auto-populate."
            }
          </p>
        </div>

        {/* Clipper / Bookmarklet Tutorial */}
        <div className="max-w-3xl mx-auto mb-6">
          <button
            onClick={() => setShowTutorial(!showTutorial)}
            className="w-full flex items-center justify-between px-5 py-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl hover:from-purple-100 hover:to-pink-100 transition-colors"
            data-testid="tutorial-toggle"
          >
            <div className="flex items-center gap-2.5">
              <HelpCircle className="h-4.5 w-4.5 text-purple-500" />
              <span className="text-sm font-semibold text-purple-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                How to use the Magic Clipper / Bookmarklet
              </span>
            </div>
            {showTutorial ? <ChevronUp className="h-4 w-4 text-purple-400" /> : <ChevronDown className="h-4 w-4 text-purple-400" />}
          </button>

          {showTutorial && (
            <div className="border border-purple-200 border-t-0 rounded-b-xl bg-white px-6 py-5 space-y-5 animate-in slide-in-from-top-2 duration-200" data-testid="tutorial-content">
              {/* Bookmark Bar Tip */}
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Bookmark className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>First, make sure your Bookmark Bar is visible</p>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2 text-xs text-amber-700">
                      <Monitor className="h-3.5 w-3.5 flex-shrink-0" />
                      <span><strong>Windows/Linux:</strong> Press <kbd className="px-1.5 py-0.5 bg-amber-100 rounded text-amber-800 font-mono text-[11px]">Ctrl + Shift + B</kbd> in Chrome</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-amber-700">
                      <Apple className="h-3.5 w-3.5 flex-shrink-0" />
                      <span><strong>Mac:</strong> Press <kbd className="px-1.5 py-0.5 bg-amber-100 rounded text-amber-800 font-mono text-[11px]">Cmd + Shift + B</kbd> in Chrome</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Setup (one-time)</h4>
                <div className="grid gap-3">
                  {[
                    { step: '1', title: 'Go to the Clipper Setup page', desc: 'Navigate to the Browser Clipper page from the settings or use the link below.', action: 'setup' },
                    { step: '2', title: 'Drag the bookmarklet to your Bookmark Bar', desc: 'You\'ll see a button labeled "RealProp Clipper" — drag it to your Chrome bookmark bar.' },
                    { step: '3', title: 'Done! You\'re ready to clip.', desc: 'The bookmarklet is now saved in your browser.' },
                  ].map(item => (
                    <div key={item.step} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.step}</div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-purple-100 pt-4 space-y-4">
                <h4 className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Using the Clipper</h4>
                <div className="grid gap-3">
                  {[
                    { step: '1', title: 'Open any property listing', desc: 'Go to Zillow, Realtor.com, Redfin, or any listing website.' },
                    { step: '2', title: 'Click the "RealProp Clipper" bookmark', desc: 'It will extract the property address, price, beds, baths, sqft, description, and photos automatically.' },
                    { step: '3', title: 'RealProp opens with the data loaded', desc: 'You\'ll land here on Magic Create with all the property data pre-filled. Just pick a template and create!' },
                  ].map(item => (
                    <div key={item.step} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-green-100 text-green-700 text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.step}</div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Link to="/clipper-setup" className="text-sm font-medium text-purple-600 hover:text-purple-700 underline underline-offset-2" data-testid="goto-clipper-setup">
                  Go to Clipper Setup Page
                </Link>
                <span className="text-xs text-gray-400">or paste a URL below to scrape directly</span>
              </div>
            </div>
          )}
        </div>

        {/* Clipper Loading State */}
        {clipperLoading && (
          <Card className="max-w-2xl mx-auto mb-8">
            <CardContent className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-500" />
              <p className="text-lg font-medium">Loading clipped property data...</p>
              <p className="text-sm text-muted-foreground mt-1">Data sent from your browser's RealProp Clipper</p>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Property Data Input */}
        {!activeListing && !clipperLoading && (
          <Card className="max-w-2xl mx-auto mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center">1</span>
                {manualMode ? 'Enter Property Details' : 'Paste Listing URL'}
              </CardTitle>
              <CardDescription>
                {manualMode 
                  ? 'Enter the property information manually'
                  : 'Works with Zillow, Realtor.com, Redfin, Realtor.ca, RE/MAX, Compass, MLS sites, and agent/broker websites'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!manualMode ? (
                <>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder="https://www.zillow.com/homedetails/..."
                      value={listingUrl}
                      onChange={(e) => setListingUrl(e.target.value)}
                      className="flex-1 min-w-0"
                      data-testid="listing-url-input"
                    />
                    <Button 
                      onClick={handleMagicCreate} 
                      disabled={isLoading}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 w-full sm:w-auto flex-shrink-0"
                      data-testid="magic-create-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-2" />
                          RealProp Magic Import
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 flex-wrap">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setManualMode(true)}
                      className="text-muted-foreground justify-start w-full sm:w-auto"
                    >
                      <Edit3 className="h-4 w-4 mr-2 flex-shrink-0" />
                      Enter details manually instead
                    </Button>
                    <span className="hidden sm:inline text-muted-foreground text-xs">or</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => navigate('/clipper-setup')}
                      className="text-muted-foreground justify-start w-full sm:w-auto"
                      data-testid="clipper-toggle-btn"
                    >
                      <Scissors className="h-4 w-4 mr-2 flex-shrink-0" />
                      Browser Clipper Setup
                    </Button>
                  </div>

                  {/* Clipper Setup Panel */}
                  {showClipper && (
                    <div className="mt-4 p-5 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-xl border border-purple-500/20" data-testid="clipper-setup-panel">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <Bookmark className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold">RealProp Clipper</h4>
                          <p className="text-xs text-muted-foreground">Works in Chrome, Safari, Firefox, Edge — any browser</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">1</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium mb-2">Drag this button to your bookmarks bar:</p>
                            {clipperBookmarklet ? (
                              <a
                                ref={bookmarkletRef}
                                href="#"
                                onClick={(e) => { e.preventDefault(); toast.info('Drag this to your bookmarks bar — don\'t click it here!'); }}
                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-medium cursor-grab active:cursor-grabbing shadow-lg hover:shadow-xl transition-shadow"
                                draggable="true"
                                data-testid="clipper-bookmarklet-link"
                              >
                                <Globe className="h-4 w-4" />
                                RealProp Clipper
                              </a>
                            ) : (
                              <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-muted rounded-lg text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-2 italic">
                              Can't drag? Copy the code below and create a bookmark manually.
                            </p>
                            {clipperBookmarklet && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7 mt-1"
                                onClick={() => {
                                  navigator.clipboard.writeText(clipperBookmarklet);
                                  toast.success('Bookmarklet code copied!');
                                }}
                              >
                                <Copy className="h-3 w-3 mr-1" /> Copy bookmarklet code
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">2</span>
                          <p className="text-sm">Browse to any property listing (Zillow, Redfin, any site) in your normal browser.</p>
                        </div>

                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-500 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">3</span>
                          <p className="text-sm">Click <strong>"RealProp Clipper"</strong> in your bookmarks bar. It reads the page, extracts data, and sends it here.</p>
                        </div>

                        <div className="p-3 bg-background rounded-lg border border-dashed border-muted-foreground/30 text-center">
                          <p className="text-xs text-muted-foreground">
                            <strong>Why does this beat automated scraping?</strong> Your browser is already logged in, has real cookies, and uses your home IP — no bot detection can block it.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Price *</Label>
                      <Input
                        placeholder="499000"
                        value={manualData.price}
                        onChange={(e) => setManualData({...manualData, price: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Address *</Label>
                      <Input
                        placeholder="123 Main St, City, State 12345"
                        value={manualData.address}
                        onChange={(e) => setManualData({...manualData, address: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Beds</Label>
                      <Input
                        type="number"
                        value={manualData.beds}
                        onChange={(e) => setManualData({...manualData, beds: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Baths</Label>
                      <Input
                        type="number"
                        value={manualData.baths}
                        onChange={(e) => setManualData({...manualData, baths: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Square Footage</Label>
                      <Input
                        value={manualData.sqft}
                        onChange={(e) => setManualData({...manualData, sqft: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Property Description</Label>
                      <textarea
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[100px] mt-1"
                        placeholder="Describe the property — upgrades, features, neighborhood highlights, lifestyle benefits... AI will auto-generate key bullet points for your marketing materials."
                        value={manualData.property_description}
                        onChange={(e) => setManualData({...manualData, property_description: e.target.value})}
                        data-testid="manual-property-description"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">
                        AI will extract key selling points for flyers, prints & social posts
                      </p>
                    </div>
                    <div className="col-span-2">
                      <Label>Listing URL (for QR code)</Label>
                      <Input
                        placeholder="https://www.zillow.com/..."
                        value={manualData.listing_url}
                        onChange={(e) => setManualData({...manualData, listing_url: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setManualMode(false)}>Cancel</Button>
                    <Button onClick={handleManualSubmit} disabled={generatingFeatures} data-testid="manual-save-btn">
                      {generatingFeatures ? (
                        <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating Features...</>
                      ) : (
                        'Save Property Data'
                      )}
                    </Button>
                  </div>

                  {/* Photo Upload in Manual Mode */}
                  <div className="mt-4 p-4 bg-background rounded-lg border border-border/30">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                      <Image className="h-4 w-4" />
                      Property Photos
                    </div>
                    <div className="border-2 border-dashed border-border/50 rounded-xl p-4 text-center mb-3" data-testid="manual-no-photos-prompt">
                      <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground/50" />
                      <p className="text-xs text-muted-foreground">Upload photos from your device or import from Google Drive / Dropbox</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        id="manual-photo-upload"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          files.forEach(file => {
                            if (!file.type.startsWith('image/')) return;
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              setManualData(prev => ({
                                ...prev,
                                photos: [...(prev.photos || []), ev.target.result]
                              }));
                            };
                            reader.readAsDataURL(file);
                          });
                        }}
                      />
                      <Button variant="outline" size="sm" onClick={() => document.getElementById('manual-photo-upload')?.click()} data-testid="manual-upload-photos-btn">
                        <Upload className="h-3.5 w-3.5 mr-1" /> Upload Photos
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowCloudImport(true)} data-testid="manual-cloud-import-btn">
                        <CloudCog className="h-3.5 w-3.5 mr-1" /> Google Drive / Dropbox
                      </Button>
                    </div>
                    {manualData.photos?.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mt-3">
                        {manualData.photos.map((photo, idx) => (
                          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-border/30">
                            <img src={photo} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />
                            <button className="absolute top-1 right-1 bg-black/50 rounded-full p-0.5" onClick={() => {
                              setManualData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));
                            }}>
                              <X className="h-3 w-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Cloud Import Modal for Manual Mode */}
                    {showCloudImport && (
                      <div className="mt-3 border border-border/50 rounded-lg p-4 bg-card" data-testid="manual-cloud-import-modal">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold">Import from Google Drive / Dropbox</h4>
                          <Button variant="ghost" size="sm" onClick={() => setShowCloudImport(false)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          Paste Google Drive or Dropbox share links (one per line). Make sure sharing is set to "Anyone with the link".
                        </p>
                        <textarea
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] mb-2"
                          placeholder={"https://drive.google.com/drive/folders/xxxxx\nhttps://drive.google.com/file/d/xxxxx/view\nhttps://www.dropbox.com/s/xxxxx/photo.jpg?dl=0"}
                          value={cloudLinks}
                          onChange={(e) => setCloudLinks(e.target.value)}
                          data-testid="manual-cloud-links-textarea"
                        />
                        <Button
                          size="sm"
                          onClick={async () => {
                            const links = cloudLinks.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                            if (!links.length) { toast.error('Enter at least one link'); return; }
                            setImportingCloud(true);
                            try {
                              const res = await apiClient.post('/magic-create/import-cloud-images', { urls: links });
                              if (res.data.success && res.data.photos.length > 0) {
                                setManualData(prev => ({
                                  ...prev,
                                  photos: [...(prev.photos || []), ...res.data.photos]
                                }));
                                toast.success(`Imported ${res.data.photos.length} image${res.data.photos.length > 1 ? 's' : ''}`);
                                setShowCloudImport(false);
                                setCloudLinks('');
                              } else {
                                toast.error('Could not resolve any links.');
                              }
                            } catch { toast.error('Failed to import'); }
                            setImportingCloud(false);
                          }}
                          disabled={importingCloud}
                          data-testid="manual-import-cloud-btn"
                        >
                          {importingCloud ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                          Import Images
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Active Listing Display */}
        {activeListing && (
          <Card className="max-w-4xl mx-auto mb-8 border-primary/30 bg-primary/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-primary">
                  <CheckCircle2 className="h-5 w-5" />
                  Property Data Loaded
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={clearListing}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Start Over
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <DollarSign className="h-4 w-4" />
                    Price
                  </div>
                  <p className="text-xl font-bold">{formatPrice(activeListing.price)}</p>
                </div>
                <div className="p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Bed className="h-4 w-4" />
                    Beds
                  </div>
                  <p className="text-xl font-bold">{activeListing.beds || '-'}</p>
                </div>
                <div className="p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Bath className="h-4 w-4" />
                    Baths
                  </div>
                  <p className="text-xl font-bold">{activeListing.baths || '-'}</p>
                </div>
                <div className="p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Square className="h-4 w-4" />
                    Sq Ft
                  </div>
                  <p className="text-xl font-bold">{activeListing.sqft || '-'}</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-background rounded-lg">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <MapPin className="h-4 w-4" />
                    Address
                  </div>
                  {activeListing.listing_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs px-2 text-violet-700 hover:text-violet-800 hover:bg-violet-50"
                      onClick={handleRefreshListing}
                      disabled={refreshingListing}
                      title="Re-scrape the source listing to update street, city, state & zip"
                      data-testid="refresh-listing-btn"
                    >
                      {refreshingListing ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                      )}
                      Refresh data
                    </Button>
                  )}
                </div>
                <p className="font-medium" data-testid="active-listing-address">{activeListing.address}</p>
                {(activeListing.city || activeListing.state || activeListing.zip) && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="active-listing-address-parts">
                    {[activeListing.city, activeListing.state, activeListing.zip].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              
              {/* Property Photos Gallery */}
              <div className="mt-4 p-4 bg-background rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Image className="h-4 w-4" />
                    Property Photos
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {(activeListing.photos?.length || 0)} photo{(activeListing.photos?.length || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {activeListing.photos && activeListing.photos.length > 0 && activeListing.has_real_photos ? (
                  <>
                    <p className="text-[11px] text-muted-foreground mb-2" data-testid="reorder-hint">
                      Drag to reorder · the <strong>Hero</strong> photo is used as the main image across all templates.
                    </p>
                    <SortablePhotoGrid
                      photos={activeListing.photos}
                      max={60}
                      onChange={(next) => setActiveListing({
                        ...activeListing,
                        photos: next,
                        main_image_url: next[0] || null,
                        has_real_photos: next.length > 0,
                      })}
                    />
                  </>
                ) : (
                  /* No photos — show upload prompt */
                  <div className="border-2 border-dashed border-border/50 rounded-xl p-6 text-center mb-3" data-testid="no-photos-prompt">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                    <p className="text-sm font-medium mb-1">No property photos found</p>
                    <p className="text-xs text-muted-foreground mb-3">Upload photos from your device or import from Google Drive / Dropbox</p>
                  </div>
                )}

                {/* Upload & Import buttons — always visible */}
                <div className="flex flex-wrap gap-2">
                  <input
                    ref={photoUploadRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                    data-testid="photo-upload-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => photoUploadRef.current?.click()}
                    data-testid="upload-photos-btn"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1" /> Upload Photos
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCloudImport(true)}
                    data-testid="cloud-import-btn"
                  >
                    <CloudCog className="h-3.5 w-3.5 mr-1" /> Google Drive / Dropbox
                  </Button>
                </div>

                {/* Cloud Import Modal */}
                {showCloudImport && (
                  <div className="mt-3 border border-border/50 rounded-lg p-4 bg-card" data-testid="cloud-import-modal">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold">Import from Google Drive / Dropbox</h4>
                      <Button variant="ghost" size="sm" onClick={() => setShowCloudImport(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Paste Google Drive or Dropbox share links (one per line). Make sure sharing is set to "Anyone with the link".
                    </p>
                    <textarea
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] mb-2"
                      placeholder={"https://drive.google.com/file/d/xxxxx/view\nhttps://www.dropbox.com/s/xxxxx/photo.jpg?dl=0"}
                      value={cloudLinks}
                      onChange={(e) => setCloudLinks(e.target.value)}
                      data-testid="cloud-links-textarea"
                    />
                    <Button
                      size="sm"
                      onClick={handleCloudImport}
                      disabled={importingCloud}
                      data-testid="import-cloud-btn"
                    >
                      {importingCloud ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                      Import Images
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Key Features */}
              {activeListing.key_features && activeListing.key_features.length > 0 && (
                <div className="mt-4 p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Key Features
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {activeListing.key_features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-primary">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Original scraped description (full text from listing site) */}
              {(activeListing.description && activeListing.description !== activeListing.property_summary) && (
                <div className="mt-4 p-4 bg-background rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                    <FileText className="h-4 w-4" />
                    Original Listing Description
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">From listing</span>
                  </div>
                  <p className="text-muted-foreground whitespace-pre-line text-sm leading-relaxed">{activeListing.description}</p>
                </div>
              )}

              {/* AI Rewrite — alternative styles user can pick from */}
              {activeListing.property_summary && (
                <div className="mt-4 p-4 bg-background rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Sparkles className="h-4 w-4" />
                      AI Rewrite
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">optional</span>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {[
                        { value: 'minimalist', label: 'Minimal' },
                        { value: 'storytelling', label: 'Story' },
                        { value: 'witty', label: 'Witty' },
                        { value: 'luxury', label: 'Luxury' },
                        { value: 'family_friendly', label: 'Family' },
                        { value: 'investor', label: 'Investor' }
                      ].map(style => (
                        <Button
                          key={style.value}
                          variant={selectedDescStyle === style.value ? 'default' : 'ghost'}
                          size="sm"
                          className="h-7 text-xs px-2"
                          onClick={() => handleRegenerateDescription(style.value)}
                          disabled={regeneratingDesc}
                          data-testid={`desc-style-${style.value}`}
                        >
                          {regeneratingDesc && selectedDescStyle === style.value ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            style.label
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <p className="text-muted-foreground">{activeListing.property_summary}</p>
                  <p className="text-[10px] text-muted-foreground mt-2">Tip: Pick a style above to rewrite — or skip and we'll use the original listing description in your caption.</p>
                </div>
              )}
              
              {/* Listing URL for QR Code */}
              {activeListing.listing_url && (
                <div className="mt-4 p-3 bg-background rounded-lg border border-dashed border-muted-foreground/30">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Link2 className="h-4 w-4" />
                    <span className="truncate">{activeListing.listing_url}</span>
                    <span className="text-xs bg-secondary px-2 py-0.5 rounded">QR Ready</span>
                  </div>
                </div>
              )}
              
              {brandKit && (
                <div className="mt-4 p-3 bg-background rounded-lg border border-primary/20">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    Brand Kit loaded - Your colors, fonts, logo, and headshot will be applied
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Content Creation Hub */}
        {activeListing && (
          <AssetTypeHub activeListing={activeListing} templates={templates} />
        )}
      </main>
      
      {/* Verify Property Details Modal */}
      <VerifyPropertyModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        propertyData={pendingPropertyData}
        verificationFields={verificationFields}
        confidence={confidence}
        onVerify={handleVerifyProperty}
      />
    </div>
  );
};

export default MagicCreatePage;
